import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import CheckIcon from '@mui/icons-material/Check';
import { formatDecimal } from '@/utils/misc';

interface NumberFieldWithCheckProps {
  id: string;
  decimals?: number;
  max?: number;
  min?: number;
  name: string;
  label?: string;
  value: number;
  onChange?: (field: string, value: number) => void;
  variant?: 'outlined' | 'standard';
  multiline?: boolean;
  rows?: number;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  showCheckIcon?: boolean;
  placeholder?: string;
}

const separator = ',';

const NumberField: React.FC<NumberFieldWithCheckProps> = ({
  id,
  name,
  decimals = 2,
  min,
  max,
  label,
  value,
  disabled,
  onChange,
  variant = 'outlined',
  multiline = false,
  rows = 1,
  helperText,
  error,
  showCheckIcon = true,
  placeholder,
}) => {
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    setInputValue(formatDecimal(value, decimals));
  }, [value, decimals]);

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let raw = event.target.value;

    // Remove all except digits and separators
    raw = raw.replace(/[^\d,.-]/g, '');
    // Replace all dots with nothing (they’re thousands separators)
    raw = raw.replace(/\./g, '');

    const normalized = raw.replace(separator, '.');
    const parsed = parseFloat(normalized);

    if (isNaN(parsed)) {
      setInputValue(raw);
      return;
    }

    if ((min !== undefined && parsed < min) || (max !== undefined && parsed > max)) {
      return;
    }

    setInputValue(raw);
    onChange?.(name, parsed);
  };

  return (
    <TextField
      id={id}
      name={name}
      label={label}
      variant={variant}
      value={inputValue}
      onChange={handleOnChange}
      placeholder={placeholder}
      InputProps={{
        endAdornment: showCheckIcon && value !== 0 ? <CheckIcon color="primary" /> : null,
      }}
      multiline={multiline}
      rows={rows}
      helperText={helperText}
      error={error}
      disabled={disabled}
      fullWidth
    />
  );
};

export default NumberField;
